import React, { useState, useEffect, useRef } from 'react';
import { FaCheckCircle, FaPlay, FaPause, FaArrowRight } from 'react-icons/fa';
import { MdDashboard, MdOutlineIntegrationInstructions, MdDataUsage } from 'react-icons/md';
import { FiTarget, FiSend, FiActivity } from 'react-icons/fi';
import { LuBrain, LuRocket } from 'react-icons/lu';

const steps = [
  {
    icon: <FiTarget className="text-2xl" />,
    title: "Requirement Analysis",
    subtitle: "Understand Your Product Vision",
    desc: "We gather your business requirements, define goals, and map out core features.",
    details: "Deep discovery sessions to align product functionality with real user needs and business objectives.",
    metrics: "Clear project roadmap",
    stat: "100% clarity",
    color: "purple"
  },
  {
    icon: <LuBrain className="text-2xl" />,
    title: "System Design",
    subtitle: "Architecture & Planning",
    desc: "Design scalable system architecture, database structure, and APIs.",
    details: "We create a strong technical foundation ensuring performance, scalability, and security.",
    metrics: "Future-ready systems",
    stat: "High scalability",
    color: "blue"
  },
  {
    icon: <MdDataUsage className="text-2xl" />,
    title: "Development",
    subtitle: "Build Core Features",
    desc: "Frontend, backend, and integrations are developed with clean and maintainable code.",
    details: "Agile development process with continuous testing and iterative improvements.",
    metrics: "Fast & reliable builds",
    stat: "Optimized code",
    color: "green"
  },
  {
    icon: <FiSend className="text-2xl" />,
    title: "Launch & Scale",
    subtitle: "Deployment & Growth",
    desc: "Deploy your application and optimize it for performance, security, and scalability.",
    details: "Ongoing monitoring, updates, and improvements to support real-world growth.",
    metrics: "Production-ready apps",
    stat: "High performance",
    color: "orange"
  },
];

const DemoSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, { threshold: 0.1 });

    const elements = sectionRef.current?.querySelectorAll('.fade-up, .fade-left, .fade-right');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setActiveStep((prevStep) => (prevStep + 1) % steps.length);
            return 0;
          }
          return prev + 1;
        });
      }, 50);
    }
    return () => clearInterval(interval);
  }, [isPlaying, steps.length]);

  const handleStepChange = (index) => {
    setActiveStep(index);
    setProgress(0);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 100);
  };

  return (
    <section id="demo" ref={sectionRef} className="py-20 bg-gradient-to-b from-[#0C0E15] to-[#0A0A0F] rounded-t-3xl rounded-b-3xl max-w-7xl mx-auto my-8 px-5 md:px-8">
      <div className="text-center fade-up">
        <span className="text-purple-400 font-semibold tracking-wide uppercase text-sm">
          Product Showcase
        </span>

        <h2 className="text-4xl md:text-5xl font-bold mt-2">
          See Your <span className="gradient-text">Software Vision</span> come to life
        </h2>

        <p className="text-gray-400 mt-3 max-w-2xl mx-auto text-lg">
          Discover how we design, build, and launch scalable software solutions that power real business growth
        </p>
      </div>

      {/* Step Tabs with Animation */}
      <div className="flex flex-wrap justify-center gap-3 mt-10 fade-up">
        {steps.map((step, i) => (
          <button
            key={i}
            onClick={() => handleStepChange(i)}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-500 flex items-center gap-2 ${activeStep === i
              ? `bg-gradient-to-r from-${step.color}-600 to-purple-600 text-white shadow-xl scale-105`
              : 'glass-card text-gray-300 hover:bg-white/10'
              }`}
          >
            {step.icon}
            {step.title}
          </button>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="max-w-3xl mx-auto mt-8 fade-up">
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>Step 1</span>
          <span>Step 2</span>
          <span>Step 3</span>
          <span>Step 4</span>
        </div>
      </div>

      {/* Step Content with Animation */}
      <div className="mt-12 glass-card rounded-2xl p-8 md:p-10 transition-all duration-500 hover:shadow-2xl">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="fade-left">
            <div className="flex items-center gap-2 mb-3">
              <div className={`p-2 rounded-lg bg-gradient-to-r from-${steps[activeStep].color}-500/20 to-purple-500/20`}>
                {steps[activeStep].icon}
              </div>
              <span className="text-sm text-purple-400 font-semibold">{steps[activeStep].subtitle}</span>
            </div>
            <h3 className="text-3xl font-bold gradient-text mb-4">{steps[activeStep].title}</h3>
            <p className="text-gray-300 text-lg leading-relaxed">{steps[activeStep].desc}</p>
            <p className="text-gray-400 mt-4 border-l-2 border-purple-500 pl-4 italic">
              {steps[activeStep].details}
            </p>

            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center gap-2 bg-green-500/20 rounded-full px-4 py-2">
                <FaCheckCircle className="text-green-400" />
                <span className="text-sm font-medium">{steps[activeStep].metrics}</span>
              </div>
              <div className="flex items-center gap-2 bg-purple-500/20 rounded-full px-4 py-2">
                <FiActivity className="text-purple-400" />
                <span className="text-sm font-medium">{steps[activeStep].stat}</span>
              </div>
            </div>

            <button className="mt-8 flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-all group">
              <FaPlay className="text-sm group-hover:scale-110 transition-transform" />
              Watch full demo
              <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="fade-right">
            <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-500">
              <div className="bg-black/40 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-400">Live AI Processing</span>
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="ml-auto text-gray-400 hover:text-white transition"
                  >
                    {isPlaying ? <FaPause className="text-xs" /> : <FaPlay className="text-xs" />}
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Data Collection</span>
                      <span>{Math.floor(progress * 0.85)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300" style={{ width: `${progress * 0.85}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>AI Analysis</span>
                      <span>{Math.floor(progress * 0.7)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-300" style={{ width: `${progress * 0.7}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Lead Scoring</span>
                      <span>{Math.floor(progress * 0.6)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-300" style={{ width: `${progress * 0.6}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-gray-300">
                    {activeStep === 0 && "🎯 Analyzing 2,847 target companies matching ICP..."}
                    {activeStep === 1 && "🤖 Scanning 50+ data sources for new prospects..."}
                    {activeStep === 2 && "📊 Enriching data with 23 intent signals..."}
                    {activeStep === 3 && "✉️ Generating personalized outreach templates..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;